import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ItemImagemService } from '../../../services/produtos/item-imagem.service';
import { getImagemPrincipalDoItem, getImagemSecundariaDoItem } from '../../utils/item-imagem.util';

/**
 * Componente de APRESENTACAO de imagens do item (principal + secundarias).
 *
 * Caracteristicas-chave:
 *  - LAZY: o GET so dispara em `ngOnChanges(itemId)`. O pai controla quando montar
 *    o componente (via *ngIf) - listagens nunca pre-carregam imagens.
 *  - Cache compartilhado no `ItemImagemService` (zero re-fetch para o mesmo item).
 *  - Carrossel CUSTOM (sem p-carousel, sem p-galleria). Suporta swipe touch.
 *  - Lightbox EMBUTIDO (sem extracao de componente).
 *  - Tema via input `[tema]` ('default' | 'publico-cardapio').
 *  - Degrade gracioso em caso de erro: mostra so a imagem principal (ou placeholder).
 *    NUNCA emite toast - falha aqui e cosmetica.
 */
@Component({
  selector: 'app-item-imagens-carrossel',
  templateUrl: './item-imagens-carrossel.component.html',
  styleUrls: ['./item-imagens-carrossel.component.scss'],
})
export class ItemImagensCarrosselComponent implements OnInit, OnChanges, OnDestroy {
  @Input() itemId: number | undefined;
  @Input() imagemPrincipalUrl: string | undefined;
  @Input() idEmpresa: string | undefined;
  @Input() tema: 'default' | 'publico-cardapio' = 'default';
  @Input() alt: string | undefined;

  /**
   * Quando `true`, exibe uma coluna/faixa de miniaturas (thumbnails) clicaveis
   * ao lado da imagem principal (estilo e-commerce / MercadoLibre), em vez do
   * carrossel simples com setas + dots.
   *
   * Default `false` = mantem o comportamento original de carrossel (nao quebra
   * os usos existentes: lista-de-produtos, vendas-produto-detalhe,
   * produto-selecionar).
   */
  @Input() mostrarMiniaturas = false;

  /**
   * Modo "faixa": exibe TODAS as imagens (principal + secundarias) lado a lado,
   * em uma faixa horizontal rolavel, sem setas/carrossel. Cada imagem abre o
   * lightbox ao ser clicada. Tem precedencia sobre [mostrarMiniaturas].
   */
  @Input() modoFaixa = false;

  /**
   * Posicao da coluna de miniaturas quando `mostrarMiniaturas = true`.
   * - `left`  : coluna vertical a esquerda da imagem (desktop).
   * - `bottom`: faixa horizontal abaixo da imagem.
   *
   * Em telas pequenas a coluna `left` colapsa automaticamente para faixa
   * horizontal abaixo da imagem (controlado via CSS).
   */
  @Input() posicaoMiniaturas: 'left' | 'bottom' = 'left';

  /**
   * Notifica o pai toda vez que o lightbox abre (`true`) ou fecha (`false`).
   *
   * Existe porque o overlay do lightbox e `position: fixed`: se QUALQUER
   * ancestral criar stacking context (ex.: `position: sticky`), o overlay fica
   * preso dentro dele e o conteudo vizinho passa a pintar por cima do backdrop.
   * O pai usa este evento para neutralizar esse ancestral enquanto o lightbox
   * estiver aberto.
   *
   * OPCIONAL: quem nao liga o binding continua funcionando como antes.
   */
  @Output() lightboxAbertoChange = new EventEmitter<boolean>();

  @HostBinding('attr.tema')
  get temaAttr(): string {
    return this.tema;
  }

  /** Marca no host o modo de exibicao (carrossel x miniaturas) para o CSS. */
  @HostBinding('attr.modo')
  get modoAttr(): string {
    return this.mostrarMiniaturas ? 'miniaturas' : 'carrossel';
  }

  /** Posicao das miniaturas (left/bottom) exposta ao CSS via atributo no host. */
  @HostBinding('attr.posicao-miniaturas')
  get posicaoMiniaturasAttr(): string {
    return this.posicaoMiniaturas;
  }

  // === Estado UI ===
  loading = false;
  error: string | null = null;
  /** URLs completas (principal primeiro, depois secundarias na ordem ASC). */
  imagens: string[] = [];
  indiceAtivo = 0;

  // === Lightbox embutido ===
  lightboxAberto = false;
  lightboxUrl: string | null = null;
  lightboxAlt = '';
  private elementoFocadoAntesLightbox: HTMLElement | null = null;

  // === Swipe touch ===
  private touchStartX = 0;
  private readonly SWIPE_THRESHOLD_PX = 50;

  /**
   * Janela de slides "vizinhos" do ativo que sao montados no DOM com `src`.
   * Slides fora dessa janela ficam vazios (placeholder) ate o usuario navegar
   * para perto deles — economiza bandwidth significativo no carregamento inicial.
   *
   * Valor 1 = pre-carrega o anterior e o proximo (suavidade na navegacao).
   */
  private readonly LAZY_WINDOW = 1;

  constructor(
    private itemImagemService: ItemImagemService,
    private translate: TranslateService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    if (!this.idEmpresa) {
      this.idEmpresa = sessionStorage.getItem('id') || undefined;
    }
    // NAO faz GET aqui - espera `ngOnChanges(itemId)`.
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemId']) {
      const valorAtual = changes['itemId'].currentValue;
      const valorAnterior = changes['itemId'].previousValue;
      if (valorAtual !== valorAnterior) {
        this.resetVisual();
        if (valorAtual !== undefined && valorAtual !== null) {
          this.carregar(valorAtual);
        }
      }
    } else if (changes['imagemPrincipalUrl'] && !changes['imagemPrincipalUrl'].firstChange) {
      // Apenas atualiza o primeiro slide sem re-fetch.
      this.rebuildImagens([]);
    }
  }

  ngOnDestroy(): void {
    this.fecharLightbox();
  }

  // ===== Carregamento =====

  private resetVisual(): void {
    this.indiceAtivo = 0;
    this.error = null;
    const principal = this.resolveImagemPrincipal();
    this.imagens = principal ? [principal] : [];
  }

  private carregar(itemId: number): void {
    this.loading = true;
    this.itemImagemService.buscarImagensSecundarias(itemId).subscribe({
      next: (lista) => {
        this.loading = false;
        const secundarias = lista
          .map((s) => getImagemSecundariaDoItem(this.idEmpresa || '', s.imagem))
          .filter((u) => !!u);
        this.rebuildImagens(secundarias);
      },
      error: () => {
        this.loading = false;
        this.error = this.translate.instant('item_imagens_secundarias.erros.erro_carregar_galeria');
        // Degrade: mantem ao menos a principal se existir.
        this.rebuildImagens([]);
        // eslint-disable-next-line no-console
        console.warn('[ItemImagensCarrossel] Falha ao buscar imagens secundarias para itemId =', itemId);
      },
    });
  }

  private rebuildImagens(secundarias: string[]): void {
    const principal = this.resolveImagemPrincipal();
    const lista = principal ? [principal, ...secundarias] : [...secundarias];
    this.imagens = lista;
    if (this.indiceAtivo >= this.imagens.length) {
      this.indiceAtivo = 0;
    }
  }

  private resolveImagemPrincipal(): string {
    const v = this.imagemPrincipalUrl;
    if (!v) return '';
    // Passthrough para valores que JA SAO uma URL montada e NAO devem ser re-prefixados:
    //  - URLs absolutas (http/https) e data URLs;
    //  - URLs relativas de caminho que comecam com '/' (ex.: '/img/{idEmpresa}/item/{arquivo}'
    //    montada pelo cardapio publico). Um NOME DE ARQUIVO cru nunca comeca com '/',
    //    entao este criterio nao quebra os usos que passam o nome cru.
    if (
      v.startsWith('http://') ||
      v.startsWith('https://') ||
      v.startsWith('data:image') ||
      v.startsWith('/')
    ) {
      return v;
    }
    // Nome de arquivo cru: monta a URL no subdir `item`
    // (contrato de lista-de-produtos, vendas-produto-detalhe e produto-selecionar).
    return getImagemPrincipalDoItem(this.idEmpresa || '', v);
  }

  // ===== Navegacao =====

  anterior(): void {
    if (this.indiceAtivo > 0) this.indiceAtivo--;
  }

  proximo(): void {
    if (this.indiceAtivo < this.imagens.length - 1) this.indiceAtivo++;
  }

  irPara(indice: number): void {
    if (indice >= 0 && indice < this.imagens.length) {
      this.indiceAtivo = indice;
    }
  }

  // ===== Swipe (touch) =====

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0]?.clientX ?? 0;
  }

  onTouchEnd(event: TouchEvent): void {
    const endX = event.changedTouches[0]?.clientX ?? 0;
    const dx = endX - this.touchStartX;
    if (Math.abs(dx) > this.SWIPE_THRESHOLD_PX) {
      dx < 0 ? this.proximo() : this.anterior();
    }
  }

  // ===== Lightbox =====

  abrirLightbox(indice?: number): void {
    if (!this.imagens.length) return;
    if (indice !== undefined) this.irPara(indice);
    this.elementoFocadoAntesLightbox = (document.activeElement as HTMLElement) || null;
    this.lightboxUrl = this.imagens[this.indiceAtivo];
    this.lightboxAlt = this.textoAlt(this.indiceAtivo);
    this.setLightboxAberto(true);
  }

  fecharLightbox(): void {
    this.setLightboxAberto(false);
    this.lightboxUrl = null;
    this.lightboxAlt = '';
    if (this.elementoFocadoAntesLightbox && typeof this.elementoFocadoAntesLightbox.focus === 'function') {
      try {
        this.elementoFocadoAntesLightbox.focus();
      } catch {
        /* ignora */
      }
    }
    this.elementoFocadoAntesLightbox = null;
  }

  /** Altera o estado do lightbox e avisa o pai apenas quando ele muda. */
  private setLightboxAberto(aberto: boolean): void {
    if (this.lightboxAberto === aberto) {
      return;
    }
    this.lightboxAberto = aberto;
    this.lightboxAbertoChange.emit(aberto);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.lightboxAberto) this.fecharLightbox();
  }

  // ===== Helpers de template =====

  /** URLs cujo carregamento falhou — exibem o placeholder de imagem. */
  private readonly urlsComErro = new Set<string>();

  /** Marca uma URL como falha de carregamento. */
  onUrlErro(url: string | undefined): void {
    if (url) this.urlsComErro.add(url);
  }

  /** Indica se a URL falhou ao carregar. */
  urlComErro(url: string | undefined): boolean {
    return !!url && this.urlsComErro.has(url);
  }

  /**
   * Texto alternativo (acessibilidade) para a imagem no indice informado.
   * No tema publico-cardapio usa a chave do cardapio (com nome do produto se `alt` for fornecido).
   */
  textoAlt(indice: number): string {
    const numero = indice + 1;
    if (this.tema === 'publico-cardapio') {
      return this.translate.instant('cardapio_digital.carrossel.alt_imagem', {
        numero,
        produto: this.alt || '',
      });
    }
    return this.translate.instant('item_imagens_secundarias.alt_foto', { numero });
  }

  textoIndicador(): string {
    if (this.tema === 'publico-cardapio') {
      return this.translate.instant('cardapio_digital.carrossel.indicador_slide', {
        atual: this.indiceAtivo + 1,
        total: this.imagens.length,
      });
    }
    return `${this.indiceAtivo + 1} / ${this.imagens.length}`;
  }

  /**
   * Retorna true se o slide no indice informado deve ter `src` montado no DOM.
   *
   * Lazy load real: apenas o slide ativo e seus vizinhos imediatos
   * (LAZY_WINDOW = 1) sao carregados. Slides distantes ficam vazios ate o
   * usuario navegar para perto, economizando bandwidth no carregamento inicial
   * (importante para o publico MEI em conexoes 4G fracas).
   *
   * Combinado com o `loading="lazy"` nativo no `<img>`, garante que mesmo se o
   * navegador decidir carregar antecipadamente, so o que esta proximo do ativo
   * sera baixado.
   */
  deveCarregarSlide(indice: number): boolean {
    return Math.abs(indice - this.indiceAtivo) <= this.LAZY_WINDOW;
  }

  /**
   * Texto alternativo da miniatura (thumbnail) no indice informado.
   * No tema publico-cardapio usa a chave especifica do cardapio.
   */
  textoAltMiniatura(indice: number): string {
    const numero = indice + 1;
    if (this.tema === 'publico-cardapio') {
      return this.translate.instant('cardapio_digital.carrossel.alt_miniatura', {
        numero,
        produto: this.alt || '',
      });
    }
    return this.translate.instant('item_imagens_secundarias.alt_foto', { numero });
  }

  tooltipAmpliar(): string {
    if (this.tema === 'publico-cardapio') {
      return this.translate.instant('cardapio_digital.carrossel.tooltip_ampliar');
    }
    return this.translate.instant('item_imagens_secundarias.tooltip_expandir');
  }
}
